import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Calendar from "../Ui_Components/Calendar";
import CustomDropdown, { DropdownOption } from "../Ui_Components/CustomDropdown";
import { BASE_URL } from "../api/url";
import {
  getUserSalesJourney,
  completeCallbackRequest,
  updateJourneyStep,
} from "../../services/sales";

export interface SalesClient {
  id: string;
  name: string;
  initials: string;
  avatarColor: string;
  avatarImage?: string;
  currentStage: "new_user" | "call_request" | "assign_nutritionist" | "schedule_meeting" | "share_meal";
  phone?: string;
  email?: string;
  joinedDate?: string;
  lastSetupUpdate?: string;
  currentLocation?: string;
  originPlace?: string;
  goal?: string;
  foodPreference?: string;
  daysStuck?: number;
  completedSteps?: Record<string, string>; // stepKey → "On 2 Apr, 2026, 08:32 PM"
  hasCallRequest?: boolean; // true only if user sent a call request from the app
  callCompleted?: boolean;  // true after call is marked done, waiting for plan purchase
  callRequestedAt?: string;  // ISO timestamp from the dashboard API (call_requested_at)
  callCompletedAt?: string;  // ISO timestamp from the dashboard API (call_completed_at)
  assignedNutritionist?: string; // e.g. "Dipanwita Biswas"
}

interface SalesDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  client: SalesClient | null;
  onOpenChat?: (client: SalesClient) => void;
  onStageAdvance?: (clientId: string, stepKey: string, completedAt: string) => void;
  onAssignNutritionist?: (clientId: string) => void;
}

// STAGE_ORDER index = timeline step index + 1 (new_user is index 0, no timeline step)
// timeline[i] is "current" when stageIdx === i, "completed" when stageIdx > i, "pending" when stageIdx < i
const STAGE_ORDER = [
  "new_user",
  "call_request",
  "assign_nutritionist",
  "schedule_meeting",
  "share_meal",
];

interface TimelineStep {
  key: string;
  pendingLabel: string;
  activeTitle: string;
  completedTitle: string;
  completedNoDataLabel: string; // shown when no completedDate exists
  activeDate: string;
  buttonLabel: string;
}

const TIMELINE_STEPS: TimelineStep[] = [
  {
    key: "call_request",
    pendingLabel: "Call request",
    activeTitle: "Call requested",
    completedTitle: "Call completed",
    completedNoDataLabel: "Call completed",
    activeDate: "1 Apr, 2026, 04:32 PM",
    buttonLabel: "Mark as done",
  },
  {
    key: "assign_nutritionist",
    pendingLabel: "Assign nutritionist",
    activeTitle: "Assign nutritionist",
    completedTitle: "Nutritionist assigned",
    completedNoDataLabel: "Not yet assigned",
    activeDate: "3 Apr, 2026, 10:15 AM",
    buttonLabel: "Assign nutritionist",
  },
  {
    key: "schedule_meeting",
    pendingLabel: "Schedule meeting",
    activeTitle: "Schedule meeting",
    completedTitle: "Meeting scheduled",
    completedNoDataLabel: "Not yet scheduled",
    activeDate: "5 Apr, 2026, 02:00 PM",
    buttonLabel: "Schedule meeting",
  },
  {
    key: "share_meal",
    pendingLabel: "Meal plan share",
    activeTitle: "Meal plan share",
    completedTitle: "Meal plan shared",
    completedNoDataLabel: "Not yet shared",
    activeDate: "7 Apr, 2026, 11:30 AM",
    buttonLabel: "Share meal plan",
  },
];

// ─── Journey API types ────────────────────────────────────────────────────────
interface JourneyCheckpoint {
  type: string;
  description: string;
  created_at: string;
  created_by: string;
  metadata: Record<string, unknown>;
}

interface JourneyStep {
  step_number: number;
  step_name: string;
  status: "completed" | "in_progress" | "pending" | "skipped";
  is_current: boolean;
  created_at: string;
  completed_at: string;
  checkpoints: JourneyCheckpoint[];
  // Step 1 fields
  plan_name?: string;
  plan_duration?: string;
  // Step 2 fields
  nutritionist_id?: number;
  nutritionist_name?: string;
  // Step 3 fields
  call_scheduled_date?: string;
  call_completed_date?: string;
}

interface UserDetails {
  name: string;
  phone_no: string;
  email: string;
  profile: string;
  joined_date: string;
  last_setup_update: string;
  current_location: string;
  origin_place: string;
  goal: string;
  food_preference: string;
}

interface JourneyResponse {
  success: string;
  message: string;
  user_id: string;
  user_details: UserDetails;
  current_step: number;
  current_step_name: string;
  progress_percentage: number;
  steps: JourneyStep[];
}

// Frontend timeline index → backend step_number
// 0=call_request→step1(plan purchase), 1=assign_nutritionist→step2, 2=schedule_meeting→step3, 3=share_meal→step4
const FRONTEND_TO_BACKEND_STEP = [1, 2, 3, 4];

const formatJourneyDate = (iso: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) +
    ", " +
    d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
  );
};

// ── Time options for the dropdown (every 30 min) ─────────────────────────────
const TIME_OPTIONS: DropdownOption[] = Array.from({ length: 48 }, (_, idx) => {
  const h = Math.floor(idx / 2);
  const m = idx % 2 === 0 ? "00" : "30";
  const hour12 = h % 12 || 12;
  const ampm = h < 12 ? "AM" : "PM";
  return {
    value: `${String(h).padStart(2, "0")}:${m}`,
    label: `${String(hour12).padStart(2, "0")}:${m} ${ampm}`,
  };
});

const formatSelectedDate = (d: Date) =>
  d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const SalesDrawer: React.FC<SalesDrawerProps> = ({
  isOpen,
  onClose,
  client,
  onOpenChat,
  onStageAdvance,
  onAssignNutritionist,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(false);
  const [avatarImgError, setAvatarImgError] = useState(false);
  const [journeySteps, setJourneySteps] = useState<JourneyStep[] | null>(null);
  const [journeyUserDetails, setJourneyUserDetails] = useState<UserDetails | null>(null);
  const [confirmingStep, setConfirmingStep] = useState<string | null>(null);
  const [callConfirmLoading, setCallConfirmLoading] = useState(false);
  const [scheduleMeetingLoading, setScheduleMeetingLoading] = useState(false);
  const [scheduleDate, setScheduleDate] = useState<Date | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarBtnRef = useRef<HTMLButtonElement>(null);
  const calendarPortalRef = useRef<HTMLDivElement>(null);
  const [scheduleTime, setScheduleTime] = useState("");
  const [meetingLink, setMeetingLink] = useState("");
  // "form" → fill in details | "scheduled" → Reschedule/Mark as done | "confirming_done" → Yes, done/X
  const [schedulingSubState, setSchedulingSubState] = useState<"form" | "rescheduling" | "scheduled" | "confirming_done">("form");
  const [scheduledDisplayText, setScheduledDisplayText] = useState("");
  const [hasRescheduled, setHasRescheduled] = useState(false);
  const [isSkipConfirming, setIsSkipConfirming] = useState(false);
  const [scheduleWasSkipped, setScheduleWasSkipped] = useState(false);

  // Close calendar when clicking outside the trigger button or the calendar popup
  useEffect(() => {
    if (!showCalendar) return;
    const handleClickOutside = (e: MouseEvent) => {
      const inBtn = calendarBtnRef.current?.contains(e.target as Node);
      const inCal = calendarPortalRef.current?.contains(e.target as Node);
      if (!inBtn && !inCal) setShowCalendar(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCalendar]);

  useEffect(() => {
    if (isOpen) {
      setAvatarImgError(false);
      setInfoExpanded(false);
      setConfirmingStep(null);
      setScheduleDate(null);
      setShowCalendar(false);
      setScheduleTime("");
      setMeetingLink("");
      setSchedulingSubState("form");
      setScheduledDisplayText("");
      setHasRescheduled(false);
      setIsSkipConfirming(false);
      setScheduleWasSkipped(false);
      setJourneyUserDetails(null);
      document.body.style.overflow = "hidden";
      requestAnimationFrame(() => setIsAnimating(true));
    } else {
      document.body.style.overflow = "unset";
      if (isAnimating) {
        const timer = setTimeout(() => setIsAnimating(false), 300);
        return () => clearTimeout(timer);
      }
    }
    return () => { document.body.style.overflow = "unset"; };
  }, [isOpen, isAnimating]);

  // Fetch journey data whenever the drawer opens for a new client.
  // For new_user stage the journey API has no steps and may not return user_details,
  // so we also call the profile API as a fallback to populate the expand section.
  useEffect(() => {
    if (!isOpen || !client) return;

    if (client.currentStage === "new_user") {
      setJourneySteps(null);
      getUserSalesJourney(client.id)
        .then((data: JourneyResponse) => {
          if (data.user_details) {
            setJourneyUserDetails(data.user_details);
          }
          setJourneySteps([]);
        })
        .catch(() => {
          setJourneySteps([]);
        });
      return;
    }

    setJourneySteps(null);
    getUserSalesJourney(client.id)
      .then((data: JourneyResponse) => {
        if (data.success === "1") {
          if (data.user_details) setJourneyUserDetails(data.user_details);
          if (data.steps?.length) {
            setJourneySteps(data.steps);
            // If step 3 (Call with nutritionist) is current/in_progress and has a scheduled call
            const step3 = data.steps.find((s) => s.step_number === 3);
            if (step3?.status === "skipped") {
              setScheduleWasSkipped(true);
            } else if (step3?.is_current || step3?.status === "in_progress") {
              const hasScheduled = step3.checkpoints.some((c) => c.type === "call_scheduled");
              if (hasScheduled) {
                setSchedulingSubState("scheduled");
                if (step3.call_scheduled_date) {
                  setScheduledDisplayText(formatJourneyDate(step3.call_scheduled_date));
                }
              }
            }
          } else {
            // API returned no steps — fall back to stage-based rendering
            setJourneySteps([]);
          }
        } else {
          // API returned no usable data — fall back to stage-based rendering
          setJourneySteps([]);
        }
      })
      .catch(() => {
        // Network / parse error — fall back to stage-based rendering
        setJourneySteps([]);
      });
  }, [isOpen, client?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen && !isAnimating) return null;
  if (!client) return null;

  // stageIdx: 0=new_user,1=call_request,2=assign_nutritionist,3=schedule_meeting,4=share_meal
  const stageIdx = STAGE_ORDER.indexOf(client.currentStage);

  // callDoneAwaitingPlan: call is marked done (either via client flag or journey API)
  // but client is still in call_request column waiting for plan purchase.
  // client.callCompleted is the primary signal — set from dashboard call_status === "Completed".
  const callDoneAwaitingPlan =
    client.currentStage === "call_request" &&
    (client.callCompleted === true ||
      (journeySteps != null &&
        journeySteps.find((s) => s.step_number === 1)?.status === "completed"));

  // Derive step status.
  // callDoneAwaitingPlan takes highest priority: call_request = completed, assign_nutritionist = current.
  // For active call_request cards (not done), step 0 is always current regardless of journey API.
  // Then journey API, then stage-based fallback.
  const getStepStatus = (i: number): "completed" | "current" | "pending" => {
    if (callDoneAwaitingPlan) {
      if (i === 0) return "completed"; // call_request done
      if (i === 1) return "current";   // assign_nutritionist next
      return "pending";
    }
    // For call_request stage: completed if call is done, current otherwise (show Mark as done)
    if (client.currentStage === "call_request" && i === 0) {
      return client.callCompleted ? "completed" : "current";
    }
    if (journeySteps && journeySteps.length > 0) {
      const backendStep = journeySteps.find((s) => s.step_number === FRONTEND_TO_BACKEND_STEP[i]);
      if (backendStep?.status === "completed" || backendStep?.status === "skipped") return "completed";
      if (backendStep?.is_current || backendStep?.status === "in_progress") return "current";
      // If the API has already identified a current/in_progress step, don't let the
      // stage-based fallback incorrectly mark a later pending step as "current" too.
      const hasApiCurrentStep = journeySteps.some((s) => s.is_current || s.status === "in_progress");
      if (hasApiCurrentStep) return "pending";
    }
    // Stage-based fallback
    const timelineIdx = stageIdx - 1;
    if (i < timelineIdx) return "completed";
    if (i === timelineIdx) return "current";
    return "pending";
  };

  // Get completion date for a timeline step from journey data, fall back to client fields
  const getStepCompletedDate = (stepKey: string): string | undefined => {
    if (journeySteps) {
      const fi = TIMELINE_STEPS.findIndex((s) => s.key === stepKey);
      const backendStep = journeySteps.find((s) => s.step_number === FRONTEND_TO_BACKEND_STEP[fi]);
      if (backendStep?.completed_at) return formatJourneyDate(backendStep.completed_at);
    }
    // For call_request, fall back to the dashboard API's call_completed_at
    if (stepKey === "call_request" && client.callCompletedAt) {
      return formatJourneyDate(client.callCompletedAt);
    }
    return client.completedSteps?.[stepKey];
  };

  // Get the relevant timestamp for "current" step cards.
  // For call_request: use the call_requested checkpoint's created_at.
  // For all others: use the backend step's created_at.
  const getStepCreatedAt = (stepKey: string): string => {
    if (journeySteps) {
      const fi = TIMELINE_STEPS.findIndex((s) => s.key === stepKey);
      const backendStep = journeySteps.find((s) => s.step_number === FRONTEND_TO_BACKEND_STEP[fi]);
      if (backendStep) {
        if (stepKey === "call_request") {
          const cp = backendStep.checkpoints.find((c) => c.type === "call_requested");
          const ts = cp?.created_at ?? backendStep.created_at;
          if (ts) return formatJourneyDate(ts);
        } else if (backendStep.created_at) {
          return formatJourneyDate(backendStep.created_at);
        }
      }
    }
    // Fall back to the dashboard API's call_requested_at passed via SalesClient
    if (stepKey === "call_request" && client.callRequestedAt) {
      return formatJourneyDate(client.callRequestedAt);
    }
    return "";
  };

  // Nutritionist name: prefer journey step 2 data
  const assignedNutritionist =
    journeySteps?.find((s) => s.step_number === 2)?.nutritionist_name ??
    client.assignedNutritionist;

  // Format Date + "HH:MM" time string → "YYYY-MM-DD HH:MM:SS" for the API
  const formatDateTimeForApi = (date: Date, time: string): string => {
    const [h, m] = time.split(":").map(Number);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(h)}:${pad(m)}:00`;
  };

  const handleConfirm = (stepKey: string) => {
    const now = new Date();
    const completedAt =
      now.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) +
      ", " +
      now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    onStageAdvance?.(client.id, stepKey, completedAt);
    setConfirmingStep(null);
  };

  const markCallRequestCompleted = (completedAtIso: string) => {
    setJourneySteps((prev) =>
      prev
        ? prev.map((s) =>
            s.step_number === 0
              ? { ...s, status: "completed" as const, completed_at: completedAtIso }
              : s
          )
        : prev
    );
  };

  const handleCallRequestConfirm = async () => {
    setCallConfirmLoading(true);
    try {
      const data = await completeCallbackRequest({
        user_id: client.id,
        callback_outcome: "purchased_plan",
      });
      if (data.success === "1" && data.callback_step?.completed_at) {
        const completedAt = formatJourneyDate(data.callback_step.completed_at);
        markCallRequestCompleted(data.callback_step.completed_at);
        onStageAdvance?.(client.id, "call_request", completedAt);
      } else {
        // Fallback: use local time
        const now = new Date();
        const isoNow = now.toISOString();
        const completedAt =
          now.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) +
          ", " +
          now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
        markCallRequestCompleted(isoNow);
        onStageAdvance?.(client.id, "call_request", completedAt);
      }
    } catch {
      // Fallback on network error
      const now = new Date();
      const isoNow = now.toISOString();
      const completedAt =
        now.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) +
        ", " +
        now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
      markCallRequestCompleted(isoNow);
      onStageAdvance?.(client.id, "call_request", completedAt);
    } finally {
      setCallConfirmLoading(false);
      setConfirmingStep(null);
    }
  };

  // Called when scheduling or rescheduling a meeting (status = in_progress)
  const handleScheduleMeetingCreate = async (isReschedule: boolean) => {
    const timeLabel = TIME_OPTIONS.find((o) => o.value === scheduleTime)?.label ?? scheduleTime;
    const dateStr = scheduleDate ? formatSelectedDate(scheduleDate) : "";
    const displayText = [dateStr, timeLabel].filter(Boolean).join(", ");

    setScheduleMeetingLoading(true);
    try {
      const body: Record<string, unknown> = {
        user_id: client.id,
        step_number: 3,
        status: "in_progress",
        call_scheduled_date: scheduleDate && scheduleTime
          ? formatDateTimeForApi(scheduleDate, scheduleTime)
          : undefined,
      };
      if (meetingLink) body.call_meet_link = meetingLink;

      await updateJourneyStep(body);
    } catch {
      // Proceed with UI update even if network fails
    } finally {
      setScheduleMeetingLoading(false);
    }

    setScheduledDisplayText(displayText);
    setShowCalendar(false);
    if (isReschedule) setHasRescheduled(true);
    setSchedulingSubState("scheduled");
    setConfirmingStep(null);
  };

  // Called when the meeting is marked as done (status = completed)
  const handleScheduleMeetingComplete = async () => {
    setScheduleMeetingLoading(true);
    try {
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, "0");
      const call_completed_date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:00`;

      await updateJourneyStep({
        user_id: client.id,
        step_number: 3,
        status: "completed",
        call_completed_date,
      });
    } catch {
      // Proceed with UI update even if network fails
    } finally {
      setScheduleMeetingLoading(false);
    }

    // Immediately reflect the completion in local state so the timeline advances
    // to show schedule_meeting as completed and share_meal as current.
    const nowIso = new Date().toISOString();
    setJourneySteps((prev) => {
      if (!prev) return prev;
      const hasStep4 = prev.some((s) => s.step_number === 4);
      const updated = prev.map((s) => {
        if (s.step_number === 3) {
          return { ...s, status: "completed" as const, completed_at: nowIso, is_current: false };
        }
        if (s.step_number === 4) {
          return { ...s, status: "in_progress" as const, is_current: true };
        }
        return s;
      });
      if (!hasStep4) {
        updated.push({
          step_number: 4,
          step_name: "share_meal",
          status: "in_progress" as const,
          is_current: true,
          created_at: nowIso,
          completed_at: "",
          checkpoints: [],
        });
      }
      return updated;
    });

    handleConfirm("schedule_meeting");
  };

  // Called when the user confirms skipping the schedule meeting step
  const handleSkipMeeting = async () => {
    setScheduleMeetingLoading(true);
    try {
      await updateJourneyStep({
        user_id: client.id,
        step_number: 3,
        status: "skipped",
      });
    } catch {
      // Proceed with UI update even if network fails
    } finally {
      setScheduleMeetingLoading(false);
    }

    const nowIso = new Date().toISOString();
    setScheduleWasSkipped(true);
    setJourneySteps((prev) => {
      if (!prev) return prev;
      const hasStep4 = prev.some((s) => s.step_number === 4);
      const updated = prev.map((s) => {
        if (s.step_number === 3) {
          return { ...s, status: "completed" as const, completed_at: nowIso, is_current: false };
        }
        if (s.step_number === 4) {
          return { ...s, status: "in_progress" as const, is_current: true };
        }
        return s;
      });
      if (!hasStep4) {
        updated.push({
          step_number: 4,
          step_name: "share_meal",
          status: "in_progress" as const,
          is_current: true,
          created_at: nowIso,
          completed_at: "",
          checkpoints: [],
        });
      }
      return updated;
    });
    setIsSkipConfirming(false);
    handleConfirm("schedule_meeting");
  };

  return createPortal(
    <div>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-500 ease-in-out ${
          isOpen ? "bg-opacity-25" : "bg-opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        style={{ zIndex: 9998 }}
      />

      {/* Panel */}
      <div
        className={`fixed right-0 top-0 bg-white shadow-2xl flex flex-col transition-transform duration-500 ease-out ${
          isOpen && isAnimating ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ zIndex: 9999, height: "100vh", width: "420px", maxWidth: "90vw" }}
      >
        {/* ── Client header (tap to expand info) ── */}
        {(() => {
          const profileSrc = journeyUserDetails?.profile
            ? `${BASE_URL}${journeyUserDetails.profile}`
            : client.avatarImage;
          const displayName = journeyUserDetails?.name ?? client.name;
          return (
            <div className="px-6 pt-6 pb-4 flex-shrink-0">
              <div className="rounded-md border border-[#E5E7EB] px-2.5 py-2">
                <button
                  onClick={() => setInfoExpanded((p) => !p)}
                  className="w-full flex items-center gap-3 text-left"
                >
                  {profileSrc && !avatarImgError ? (
                    <img
                      src={profileSrc}
                      alt={displayName}
                      className="w-9 h-9 md:w-10 md:h-10 xl:w-11 xl:h-11 3xl:w-12 3xl:h-12 rounded-full object-cover flex-shrink-0"
                      onError={() => setAvatarImgError(true)}
                    />
                  ) : (
                    <div
                      className="w-9 h-9 md:w-10 md:h-10 xl:w-11 xl:h-11 3xl:w-12 3xl:h-12 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: client.avatarColor }}
                    >
                      <span className="text-white text-sm md:text-base font-semibold font-inter">
                        {client.initials}
                      </span>
                    </div>
                  )}
                  <h2 className="flex-1 text-sm md:text-base xl:text-[17px] 3xl:text-lg font-semibold font-inter text-[#1E1F26]">
                    {displayName}
                  </h2>
                  {/* Chevron */}
                  <svg
                    width="16" height="16" viewBox="0 0 24 24" fill="none"
                    stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    className="flex-shrink-0 transition-transform duration-300"
                    style={{ transform: infoExpanded ? "rotate(180deg)" : "rotate(0deg)" }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {/* Collapsible info grid */}
                <div
                  className="overflow-hidden transition-all duration-300 ease-in-out"
                  style={{ maxHeight: infoExpanded ? "500px" : "0px", opacity: infoExpanded ? 1 : 0 }}
                >
                  <div className="pt-3 mt-3">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                      <div>
                        <p className="text-[10px] md:text-[11px] xl:text-xs 3xl:text-sm text-[#9CA3AF] font-inter mb-0.5">Phone no. :</p>
                        <p className="text-xs md:text-[13px] xl:text-sm 3xl:text-base font-medium font-inter text-[#1E1F26]">
                          {journeyUserDetails?.phone_no || client.phone || "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] md:text-[11px] xl:text-xs 3xl:text-sm text-[#9CA3AF] font-inter mb-0.5">Email ID :</p>
                        <p className="text-xs md:text-[13px] xl:text-sm 3xl:text-base font-medium font-inter text-[#1E1F26] break-all">
                          {journeyUserDetails?.email || client.email || "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] md:text-[11px] xl:text-xs 3xl:text-sm text-[#9CA3AF] font-inter mb-0.5">Joined date :</p>
                        <p className="text-xs md:text-[13px] xl:text-sm 3xl:text-base font-medium font-inter text-[#1E1F26]">
                          {journeyUserDetails?.joined_date || client.joinedDate || "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] md:text-[11px] xl:text-xs 3xl:text-sm text-[#9CA3AF] font-inter mb-0.5">Last setup update :</p>
                        <p className="text-xs md:text-[13px] xl:text-sm 3xl:text-base font-medium font-inter text-[#1E1F26]">
                          {journeyUserDetails?.last_setup_update || client.lastSetupUpdate || "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] md:text-[11px] xl:text-xs 3xl:text-sm text-[#9CA3AF] font-inter mb-0.5">Current location :</p>
                        <p className="text-xs md:text-[13px] xl:text-sm 3xl:text-base font-medium font-inter text-[#1E1F26]">
                          {journeyUserDetails?.current_location || client.currentLocation || "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] md:text-[11px] xl:text-xs 3xl:text-sm text-[#9CA3AF] font-inter mb-0.5">Origin place :</p>
                        <p className="text-xs md:text-[13px] xl:text-sm 3xl:text-base font-medium font-inter text-[#1E1F26]">
                          {journeyUserDetails?.origin_place || client.originPlace || "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] md:text-[11px] xl:text-xs 3xl:text-sm text-[#9CA3AF] font-inter mb-0.5">Goal :</p>
                        <p className="text-xs md:text-[13px] xl:text-sm 3xl:text-base font-medium font-inter text-[#00A9A9]">
                          {journeyUserDetails?.goal || client.goal || "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] md:text-[11px] xl:text-xs 3xl:text-sm text-[#9CA3AF] font-inter mb-0.5">Food preference :</p>
                        <p className="text-xs md:text-[13px] xl:text-sm 3xl:text-base font-medium font-inter text-[#1E1F26]">
                          {journeyUserDetails?.food_preference || client.foodPreference || "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Timeline ── */}
        <div className="flex-1 overflow-y-auto px-6 pb-4">
          <p className="text-xs md:text-[13px] xl:text-sm 3xl:text-base font-medium text-[#6B7280] font-inter mb-3 mt-3">
            Timeline
          </p>

          {/* Loading skeleton — shown until journey API responds */}
          {journeySteps === null ? (
            <div className="flex flex-col gap-2.5">
              {[1, 2, 3, 4].map((n) => (
                <div
                  key={n}
                  className="h-12 rounded-md bg-[#F3F4F6] animate-pulse"
                />
              ))}
            </div>
          ) : (
          <div className="flex flex-col gap-2.5">
            {TIMELINE_STEPS.map((step, i) => {
              const status = getStepStatus(i);
              const isConfirming = confirmingStep === step.key;

              /* ── COMPLETED ── */
              if (status === "completed") {
                const completedDate = getStepCompletedDate(step.key);
                const displayTitle =
                  step.key === "assign_nutritionist" && assignedNutritionist
                    ? `${assignedNutritionist} assigned`
                    : step.key === "schedule_meeting" && scheduleWasSkipped
                    ? "Meeting skipped"
                    : step.completedTitle;
                return (
                  <div
                    key={step.key}
                    className="flex items-start gap-3 px-4 py-3.5 rounded-md border border-[#E5E7EB] bg-white"
                  >
                    {/* Teal circle checkmark */}
                    <div className="w-5 h-5 rounded-full border-2 border-[#00A9A9] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                        <polyline
                          points="2,6 5,9 10,3"
                          stroke="#00A9A9"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs md:text-sm xl:text-[14px] 3xl:text-base font-semibold font-inter text-[#1E1F26]">
                        {displayTitle}
                      </p>
                      <p className="text-[10px] md:text-[11px] xl:text-xs font-inter text-[#9CA3AF] mt-0.5">
                        {completedDate ? `On ${completedDate}` : step.completedNoDataLabel}
                      </p>
                    </div>
                  </div>
                );
              }

              /* ── CURRENT ── */
              if (status === "current") {
                // Show plain pending when: call is done but still awaiting plan purchase
                // (assign_nutritionist becomes "current" via the timelineIdx shift but no action yet)
                const isAwaitingPlanStep = callDoneAwaitingPlan && step.key === "assign_nutritionist";

                if (!isAwaitingPlanStep) {
                  /* ── Schedule meeting — 3-state card ── */
                  if (step.key === "schedule_meeting") {
                    // Shared header used in all 3 states
                    const cardHeader = (
                      <>
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <div className="w-2 h-2 rounded-full bg-[#00A9A9] flex-shrink-0" />
                          <span className="text-[9px] font-semibold font-inter text-[#00A9A9] bg-[#C8EFEF] px-1.5 py-0.5 rounded-full leading-none">
                            Current
                          </span>
                        </div>
                        <p className="text-xs md:text-[13px] xl:text-sm font-bold font-inter text-[#1E1F26] mb-0.5">
                          {step.activeTitle}
                        </p>
                      </>
                    );

                    /* ── State 1: form ── */
                    if (schedulingSubState === "form") {
                      return (
                        <div key={step.key} className="px-3.5 py-3 rounded-md border border-[#00A9A9] bg-[#F0FDFB]">
                          {cardHeader}
                          <p className="text-[9px] md:text-[10px] xl:text-[11px] font-inter text-[#9CA3AF] mb-3">
                            Setup a consultation call
                          </p>

                          {/* Date + Time */}
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <div>
                              <p className="text-[10px] font-inter text-[#6B7280] mb-1">Select date</p>
                              <div className="relative">
                                <button
                                  ref={calendarBtnRef}
                                  type="button"
                                  onClick={() => setShowCalendar((v) => !v)}
                                  className="w-full px-3 py-2 rounded-md border border-[#E0E0E0] bg-white text-sm font-inter text-left flex items-center justify-between focus:outline-none focus:border-[#00A9A9] hover:border-[#00A9A9] transition-colors"
                                >
                                  <span className={scheduleDate ? "text-[#1F1F1F]" : "text-[#C7C7CC]"}>
                                    {scheduleDate ? formatSelectedDate(scheduleDate) : "Select date"}
                                  </span>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                  </svg>
                                </button>
                                {showCalendar && (() => {
                                  const r = calendarBtnRef.current?.getBoundingClientRect();
                                  return createPortal(
                                    <div
                                      ref={calendarPortalRef}
                                      style={{ position: "fixed", top: r ? r.bottom + 6 : 0, left: r ? r.left : 0, zIndex: 10002 }}
                                    >
                                      <Calendar
                                        selectedDate={scheduleDate ?? new Date()}
                                        onDateSelect={(date) => { setScheduleDate(date); setShowCalendar(false); }}
                                        minDate={new Date()}
                                      />
                                    </div>,
                                    document.body
                                  );
                                })()}
                              </div>
                            </div>
                            <div onClick={() => setShowCalendar(false)}>
                              <p className="text-[10px] font-inter text-[#6B7280] mb-1">Select time</p>
                              <CustomDropdown
                                options={TIME_OPTIONS}
                                value={scheduleTime}
                                onChange={setScheduleTime}
                                placeholder="Select time"
                                maxHeight="200px"
                                zIndex={10001}
                              />
                            </div>
                          </div>

                          {/* Meeting link */}
                          <div className="mb-3">
                            <p className="text-[10px] font-inter text-[#6B7280] mb-1">
                              Meeting link <span className="text-[#9CA3AF]">(Optional)</span>
                            </p>
                            <input
                              type="url"
                              value={meetingLink}
                              onChange={(e) => setMeetingLink(e.target.value)}
                              onFocus={() => setShowCalendar(false)}
                              placeholder="Paste link"
                              className="w-full px-3 py-2 rounded-md border border-[#E0E0E0] bg-white text-sm font-inter text-[#1E1F26] placeholder-[#C7C7CC] focus:outline-none focus:border-[#00A9A9] hover:border-[#00A9A9] transition-colors"
                            />
                          </div>

                          {/* Skip · Reset · Schedule → double-check Confirm/X */}
                          <div className="flex items-center justify-between">
                            {!isSkipConfirming && (
                              <button
                                onClick={() => setIsSkipConfirming(true)}
                                className="text-[11px] font-inter text-[#6B7280] hover:text-[#1E1F26] transition-colors"
                              >
                                Skip
                              </button>
                            )}
                            <div className={`flex gap-1.5 ${isSkipConfirming ? "ml-auto" : ""}`}>
                              {isSkipConfirming ? (
                                <>
                                  <button
                                    onClick={handleSkipMeeting}
                                    disabled={scheduleMeetingLoading}
                                    className="py-1.5 px-3.5 rounded-md bg-[#00A9A9] text-white text-[11px] font-semibold font-inter hover:bg-[#008F8F] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                  >
                                    {scheduleMeetingLoading ? "Saving..." : "Yes, skip"}
                                  </button>
                                  <button
                                    onClick={() => setIsSkipConfirming(false)}
                                    disabled={scheduleMeetingLoading}
                                    className="w-7 h-7 flex items-center justify-center rounded-md border border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                  >
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                  </button>
                                </>
                              ) : isConfirming ? (
                                <>
                                  <button
                                    onClick={() => handleScheduleMeetingCreate(false)}
                                    disabled={scheduleMeetingLoading}
                                    className="py-1.5 px-[26px] rounded-md bg-[#00A9A9] text-white text-[11px] font-semibold font-inter hover:bg-[#008F8F] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                  >
                                    {scheduleMeetingLoading ? "Saving..." : "Confirm"}
                                  </button>
                                  <button
                                    onClick={() => setConfirmingStep(null)}
                                    disabled={scheduleMeetingLoading}
                                    className="w-7 h-7 flex items-center justify-center rounded-md border border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                  >
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => { setScheduleDate(null); setShowCalendar(false); setScheduleTime(""); setMeetingLink(""); }}
                                    className="py-1.5 px-3.5 rounded-md border border-[#E5E7EB] bg-white text-[11px] font-semibold font-inter text-[#6B7280] hover:bg-gray-50 transition-colors"
                                  >
                                    Reset
                                  </button>
                                  <button
                                    onClick={() => setConfirmingStep(step.key)}
                                    className="py-1.5 px-3.5 rounded-md bg-[#00A9A9] text-white text-[11px] font-semibold font-inter hover:bg-[#008F8F] transition-colors"
                                  >
                                    Schedule
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }

                    /* ── Shared info line for states 2, 3 & 4 ── */
                    const infoLine = `${assignedNutritionist ?? "Nutritionist"} as primary nutritionist${scheduledDisplayText ? " on " + scheduledDisplayText : ""}`;

                    /* ── State: rescheduling — Re-schedule meeting form ── */
                    if (schedulingSubState === "rescheduling") {
                      return (
                        <div key={step.key} className="px-3.5 py-3 rounded-md border border-[#00A9A9] bg-[#F0FDFB]">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <div className="w-2 h-2 rounded-full bg-[#00A9A9] flex-shrink-0" />
                            <span className="text-[9px] font-semibold font-inter text-[#00A9A9] bg-[#C8EFEF] px-1.5 py-0.5 rounded-full leading-none">Current</span>
                          </div>
                          <p className="text-xs md:text-[13px] xl:text-sm font-bold font-inter text-[#1E1F26] mb-0.5">
                            Re-schedule meeting
                          </p>
                          <p className="text-[9px] md:text-[10px] xl:text-[11px] font-inter text-[#9CA3AF] mb-3">
                            Setup a consultation call
                          </p>

                          {/* Date + Time */}
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <div>
                              <p className="text-[10px] font-inter text-[#6B7280] mb-1">Select date</p>
                              <div className="relative">
                                <button
                                  ref={calendarBtnRef}
                                  type="button"
                                  onClick={() => setShowCalendar((v) => !v)}
                                  className="w-full px-3 py-2 rounded-md border border-[#E0E0E0] bg-white text-sm font-inter text-left flex items-center justify-between focus:outline-none focus:border-[#00A9A9] hover:border-[#00A9A9] transition-colors"
                                >
                                  <span className={scheduleDate ? "text-[#1F1F1F]" : "text-[#C7C7CC]"}>
                                    {scheduleDate ? formatSelectedDate(scheduleDate) : "Select date"}
                                  </span>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                  </svg>
                                </button>
                                {showCalendar && (() => {
                                  const r = calendarBtnRef.current?.getBoundingClientRect();
                                  return createPortal(
                                    <div ref={calendarPortalRef} style={{ position: "fixed", top: r ? r.bottom + 6 : 0, left: r ? r.left : 0, zIndex: 10002 }}>
                                      <Calendar selectedDate={scheduleDate ?? new Date()} onDateSelect={(date) => { setScheduleDate(date); setShowCalendar(false); }} minDate={new Date()} />
                                    </div>,
                                    document.body
                                  );
                                })()}
                              </div>
                            </div>
                            <div onClick={() => setShowCalendar(false)}>
                              <p className="text-[10px] font-inter text-[#6B7280] mb-1">Select time</p>
                              <CustomDropdown options={TIME_OPTIONS} value={scheduleTime} onChange={setScheduleTime} placeholder="Select time" maxHeight="200px" zIndex={10001} />
                            </div>
                          </div>

                          {/* Meeting link */}
                          <div className="mb-3">
                            <p className="text-[10px] font-inter text-[#6B7280] mb-1">
                              Meeting link <span className="text-[#9CA3AF]">(Optional)</span>
                            </p>
                            <input
                              type="url"
                              value={meetingLink}
                              onChange={(e) => setMeetingLink(e.target.value)}
                              onFocus={() => setShowCalendar(false)}
                              placeholder="Paste link"
                              className="w-full px-3 py-2 rounded-md border border-[#E0E0E0] bg-white text-sm font-inter text-[#1E1F26] placeholder-[#C7C7CC] focus:outline-none focus:border-[#00A9A9] hover:border-[#00A9A9] transition-colors"
                            />
                          </div>

                          {/* Cancel · Update → Confirm / X */}
                          <div className="flex items-center justify-end gap-1.5">
                            {isConfirming ? (
                              <>
                                <button
                                  onClick={() => handleScheduleMeetingCreate(true)}
                                  disabled={scheduleMeetingLoading}
                                  className="py-1.5 px-[26px] rounded-md bg-[#00A9A9] text-white text-[11px] font-semibold font-inter hover:bg-[#008F8F] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                  {scheduleMeetingLoading ? "Saving..." : "Confirm"}
                                </button>
                                <button
                                  onClick={() => setConfirmingStep(null)}
                                  disabled={scheduleMeetingLoading}
                                  className="w-7 h-7 flex items-center justify-center rounded-md border border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                  </svg>
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => { setSchedulingSubState("scheduled"); setConfirmingStep(null); }}
                                  className="py-1.5 px-3.5 rounded-md border border-[#E5E7EB] bg-white text-[11px] font-semibold font-inter text-[#6B7280] hover:bg-gray-50 transition-colors"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => setConfirmingStep(step.key)}
                                  className="py-1.5 px-3.5 rounded-md bg-[#00A9A9] text-white text-[11px] font-semibold font-inter hover:bg-[#008F8F] transition-colors"
                                >
                                  Update
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    }

                    /* ── State: scheduled — Reschedule (once only) / Mark as done ── */
                    if (schedulingSubState === "scheduled") {
                      return (
                        <div key={step.key} className="px-3.5 py-3 rounded-md border border-[#00A9A9] bg-[#F0FDFB]">
                          {cardHeader}
                          <p className="text-[9px] md:text-[10px] xl:text-[11px] font-inter text-[#9CA3AF] mb-3">
                            {infoLine}
                          </p>
                          <div className="flex items-center gap-1.5">
                            {!hasRescheduled && (
                              <button
                                onClick={() => { setConfirmingStep(null); setSchedulingSubState("rescheduling"); }}
                                className="py-1.5 px-3.5 rounded-md border border-[#E5E7EB] bg-white text-[11px] font-semibold font-inter text-[#6B7280] hover:bg-gray-50 transition-colors"
                              >
                                Reschedule
                              </button>
                            )}
                            <button
                              onClick={() => setSchedulingSubState("confirming_done")}
                              className="py-1.5 px-3.5 rounded-md bg-[#00A9A9] text-white text-[11px] font-semibold font-inter hover:bg-[#008F8F] transition-colors"
                            >
                              Mark as done
                            </button>
                          </div>
                        </div>
                      );
                    }

                    /* ── State 3: confirming done — Yes, done / X ── */
                    return (
                      <div key={step.key} className="px-3.5 py-3 rounded-md border border-[#00A9A9] bg-[#F0FDFB]">
                        {cardHeader}
                        <p className="text-[9px] md:text-[10px] xl:text-[11px] font-inter text-[#9CA3AF] mb-3">
                          {infoLine}
                        </p>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => handleScheduleMeetingComplete()}
                            disabled={scheduleMeetingLoading}
                            className="py-1.5 px-3.5 rounded-md bg-[#00A9A9] text-white text-[11px] font-semibold font-inter hover:bg-[#008F8F] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {scheduleMeetingLoading ? "Saving..." : "Yes, done"}
                          </button>
                          <button
                            onClick={() => setSchedulingSubState("scheduled")}
                            disabled={scheduleMeetingLoading}
                            className="w-7 h-7 flex items-center justify-center rounded-md border border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    );
                  }

                  /* ── Meal plan share — info-only card ── */
                  if (step.key === "share_meal") {
                    return (
                      <div
                        key={step.key}
                        className="px-3.5 py-3 rounded-md border border-[#00A9A9] bg-[#F0FDFB]"
                      >
                        {/* Dot + "Current" badge */}
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <div className="w-2 h-2 rounded-full bg-[#00A9A9] flex-shrink-0" />
                          <span className="text-[9px] font-semibold font-inter text-[#00A9A9] bg-[#C8EFEF] px-1.5 py-0.5 rounded-full leading-none">
                            Current
                          </span>
                        </div>
                        {/* Title */}
                        <p className="text-xs md:text-[13px] xl:text-sm font-bold font-inter text-[#1E1F26] mb-0.5">
                          {step.activeTitle}
                        </p>
                        {/* Subtitle */}
                        <p className="text-[9px] md:text-[10px] xl:text-[11px] font-inter text-[#9CA3AF]">
                          {`${assignedNutritionist ?? "Nutritionist"} · Curating personalized meal plan`}
                        </p>
                      </div>
                    );
                  }

                  /* ── All other current steps → simple action card ── */
                  return (
                    <div
                      key={step.key}
                      className="px-3.5 py-3 rounded-md border border-[#00A9A9] bg-[#F0FDFB]"
                    >
                      {/* Dot + "Current" badge */}
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <div className="w-2 h-2 rounded-full bg-[#00A9A9] flex-shrink-0" />
                        <span className="text-[9px] font-semibold font-inter text-[#00A9A9] bg-[#C8EFEF] px-1.5 py-0.5 rounded-full leading-none">
                          Current
                        </span>
                      </div>
                      {/* Title */}
                      <p className="text-xs md:text-[13px] xl:text-sm font-bold font-inter text-[#1E1F26] mb-0.5">
                        {step.activeTitle}
                      </p>
                      {/* Date */}
                      <p className="text-[9px] md:text-[10px] xl:text-[11px] font-inter text-[#9CA3AF] mb-2.5">
                        {getStepCreatedAt(step.key) ? `On ${getStepCreatedAt(step.key)}` : ""}
                      </p>
                      {/* Buttons */}
                      {step.key === "assign_nutritionist" ? (
                        <button
                          onClick={() => onAssignNutritionist?.(client.id)}
                          className="py-1.5 px-3 rounded-md bg-[#00A9A9] text-white text-[11px] font-semibold font-inter hover:bg-[#008F8F] transition-colors"
                        >
                          {step.buttonLabel}
                        </button>
                      ) : isConfirming ? (
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => step.key === "call_request" ? handleCallRequestConfirm() : handleConfirm(step.key)}
                            disabled={callConfirmLoading}
                            className="py-1.5 px-[26px] rounded-md bg-[#00A9A9] text-white text-[11px] font-semibold font-inter hover:bg-[#008F8F] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {callConfirmLoading && step.key === "call_request" ? "Saving..." : "Confirm"}
                          </button>
                          <button
                            onClick={() => setConfirmingStep(null)}
                            disabled={callConfirmLoading}
                            className="w-7 h-7 flex items-center justify-center rounded-md border border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmingStep(step.key)}
                          className="py-1.5 px-3 rounded-md bg-[#00A9A9] text-white text-[11px] font-semibold font-inter hover:bg-[#008F8F] transition-colors"
                        >
                          {step.buttonLabel}
                        </button>
                      )}
                    </div>
                  );
                }

                // Plain pending style — used when awaiting plan purchase (no action card)
                return (
                  <div
                    key={step.key}
                    className="flex items-center gap-3 px-4 py-3.5 rounded-md bg-[#F9FAFB]"
                  >
                    <div className="w-2.5 h-2.5 rounded-full bg-[#D1D5DB] flex-shrink-0" />
                    <span className="text-xs md:text-sm xl:text-[14px] 3xl:text-base font-inter text-[#6B7280]">
                      {step.pendingLabel}
                    </span>
                  </div>
                );
              }

              /* ── PENDING ── */
              return (
                <div
                  key={step.key}
                  className="flex items-center gap-3 px-4 py-3.5 rounded-md bg-[#F9FAFB]"
                >
                  <div className="w-2.5 h-2.5 rounded-full bg-[#D1D5DB] flex-shrink-0" />
                  <span className="text-xs md:text-sm xl:text-[14px] 3xl:text-base font-inter text-[#6B7280]">
                    {step.pendingLabel}
                  </span>
                </div>
              );
            })}
          </div>
          )}
        </div>

        {/* ── Open Chat ── */}
        <div className="px-6 py-4 border-t border-[#F0F0F0] flex-shrink-0">
          <button
            onClick={() => onOpenChat?.(client)}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-md border border-[#E5E7EB] text-xs md:text-sm xl:text-[14px] 3xl:text-base font-medium font-inter text-[#1E1F26] hover:bg-gray-50 transition-colors"
          >
            Open chat
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SalesDrawer;
